import { useId, type ReactNode } from "react";

import "./motion/pixel-motion.css";

import { cx } from "@/lib/cx";

/**
 * A progress **ring** — the circular counterpart of {@link Progress}.
 *
 * Two surfaces drew this independently and neither knew the other had. The
 * dashboard's XP ring was an SVG arc on a `fill-3` track; the stats level hero
 * was a `conic-gradient` with an opaque disc absolutely positioned over the
 * middle to punch the hole, on a `fill-1` track. Same object, same label, same
 * big number in the middle, two implementations and two track tiers.
 *
 * The SVG one is the one that survived, and not by seniority:
 *
 * - **The hole is really a hole.** The conic version's "hole" was a disc painted
 *   `bg-grouped-surface`, so the component only worked on that exact background
 *   — its own author flagged that moving it to another surface would silently
 *   paint a coloured disc over the middle. An SVG arc is `fill="none"`: whatever
 *   is behind the ring shows through, on a card, on a material, in either theme.
 * - **`mask` is not needed**, which was the reason the conic version existed at
 *   all — mask support is not universal across the three shipping targets.
 * - The track is `--busee-fill-3`, the same tier `Progress` uses, so the app has
 *   one track colour rather than one per shape. The fill tiers are alpha, so it
 *   composites correctly wherever the ring lands.
 *
 * Colour goes through `style` because SVG stroke consumes the canonical
 * Material accent variable directly; the circular and linear progress marks
 * therefore cannot drift into separate teal recipes.
 *
 * ## Accessibility: the ring is a leaf, the number is its sibling
 *
 * `role="progressbar"` is a **leaf** role — assistive technology drops anything
 * rendered inside it. So the ring layer carries the role and `children` sit
 * beside it, never within. Pass `label` when the ring is the only place a value
 * is stated; omit it when the caller already writes every number the ring
 * encodes in text next to it, and the ring is then correctly `aria-hidden`
 * decoration. An explicitly labelled ring announces the real value pair.
 */

/**
 * The geometry is fixed rather than parameterised: a 104-unit box with r=44 and
 * an 8-unit stroke puts the outer edge at 100 of 104, so nothing clips, and the
 * stroke lands at ~8.6 device px at the 112px size — the weight at which the
 * alpha track is still visible at 0% and the arc still reads as a ring rather
 * than as a disc. The box is scaled by `className`, so the proportions hold at
 * every size the app uses it at.
 */
const RADIUS = 44;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

export type GaugeProps = {
  /** 0–1, clamped. */
  value: number;
  /**
   * The accessible name. With it the ring is a `progressbar`; without it the
   * ring is decoration and the caller is responsible for stating the value in
   * text beside it.
   */
  label?: string;
  /** The raw pair behind `value`, when the numbers themselves are meaningful. */
  now?: number;
  max?: number;
  /** Spoken form, e.g. "320 of 500 XP toward level 30". */
  valueText?: string;
  /** What sits in the hole. Rendered as the ring's sibling — see above. */
  children?: ReactNode;
  /** Overrides the default 88px box, for example `size-28` on Stats. */
  className?: string;
};

export function Gauge({ value, label, now, max, valueText, children, className }: GaugeProps) {
  const clipId = useId().replace(/:/g, "");
  const fraction = Math.min(Math.max(value, 0), 1);
  // Any real progress keeps a visible arc: 3 XP into a 500 XP level rounds to
  // 0% and draws an empty ring, which reads as "you have earned nothing".
  const drawn = fraction > 0 ? Math.max(fraction, 0.025) : 0;
  return (
    <span
      className={cx(
        "relative flex shrink-0 items-center justify-center",
        className ?? "size-[88px]",
      )}
    >
      <svg
        viewBox="0 0 104 104"
        // Twelve o'clock is where a dial starts; SVG's 0° is three o'clock.
        className="size-full -rotate-90"
        {...(label
          ? {
              role: "progressbar",
              "aria-label": label,
              "aria-valuemin": 0,
              "aria-valuemax": max ?? 100,
              "aria-valuenow": now ?? Math.round(fraction * 100),
              ...(valueText ? { "aria-valuetext": valueText } : {}),
            }
          : { "aria-hidden": true })}
      >
        <defs>
          <clipPath id={`${clipId}-first`}><rect x="0" y="52" width="104" height="52" /></clipPath>
          <clipPath id={`${clipId}-second`}><rect x="0" y="0" width="104" height="52" /></clipPath>
        </defs>
        <circle
          cx="52"
          cy="52"
          r={RADIUS}
          fill="none"
          strokeWidth="8"
          style={{ stroke: "var(--busee-fill-3)" }}
        />
        <g clipPath={`url(#${clipId}-first)`}>
          <circle
            cx="52" cy="52" r={RADIUS} fill="none" strokeWidth="8"
            strokeDasharray={`${CIRCUMFERENCE / 2} ${CIRCUMFERENCE}`}
            className="busee-ring-half"
            style={{ stroke: "var(--busee-accent)", transform: `rotate(${-180 + 180 * Math.min(drawn * 2, 1)}deg)` }}
          />
        </g>
        <g clipPath={`url(#${clipId}-second)`}>
          <circle
            cx="52" cy="52" r={RADIUS} fill="none" strokeWidth="8"
            strokeDasharray={`${CIRCUMFERENCE / 2} ${CIRCUMFERENCE}`}
            strokeDashoffset={-CIRCUMFERENCE / 2}
            className="busee-ring-half busee-ring-half-second"
            style={{ stroke: "var(--busee-accent)", transform: `rotate(${-180 + 180 * Math.max(drawn * 2 - 1, 0)}deg)` }}
          />
        </g>
      </svg>

      {children ? (
        <span className="absolute inset-0 flex flex-col items-center justify-center">
          {children}
        </span>
      ) : null}
    </span>
  );
}
